import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { useCallback, useId, useMemo, useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { cn } from '@/_shared/lib/utils';

import { suggestRepos } from '../lib/repoSuggestions';
import { type Repo, toRepoKey } from './repo-utils';
import { ScmProviderIcon } from './ScmProviderIcon';

export type { Repo };

/**
 * At most this many rows render below the suggestions. A workspace can see a thousand repositories, and
 * rendering every one makes the popover slow to open and to filter; search narrows the list instead.
 */
export const REPO_RENDER_LIMIT = 100;

interface RepoComboboxProps {
  repos: Repo[];
  value: string;
  onChange: (repoKey: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Wired by a `FormControl` so the trigger takes the field's label and error. */
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
}

export function RepoCombobox({
  repos,
  value,
  onChange,
  placeholder = 'Select repository...',
  disabled = false,
  id,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedBy,
}: RepoComboboxProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const suggestedId = useId();
  const allId = useId();

  const filtered = useMemo(() => {
    if (!search.trim()) return repos;
    const q = search.toLowerCase();
    return repos.filter((r) => r.fullName.toLowerCase().includes(q));
  }, [repos, search]);

  // ACT2-1108: names that look like a dbt or Profiles project go on top until the person searches.
  const suggested = useMemo(() => (search.trim() ? [] : suggestRepos(repos)), [repos, search]);
  const rest = useMemo(() => {
    if (suggested.length === 0) return filtered;
    const suggestedKeys = new Set(suggested.map(toRepoKey));
    return filtered.filter((r) => !suggestedKeys.has(toRepoKey(r)));
  }, [filtered, suggested]);
  const shown = rest.slice(0, REPO_RENDER_LIMIT);

  const selectedRepo = useMemo(() => repos.find((r) => toRepoKey(r) === value), [repos, value]);

  const handleSelect = useCallback(
    (repo: Repo) => {
      const key = toRepoKey(repo);
      if (key !== value) onChange(key);
      setOpen(false);
      setSearch('');
    },
    [onChange, value],
  );

  const renderOption = (repo: Repo): React.ReactElement => {
    const key = toRepoKey(repo);
    const selected = key === value;
    return (
      <button
        type="button"
        key={key}
        role="option"
        aria-selected={selected}
        onClick={() => handleSelect(repo)}
        // Mirror ui/SelectItem: grey-50 hover, grey-800 text, check on the right.
        className={cn(
          'flex h-8 w-full cursor-default items-center justify-between gap-2 rounded-md px-2 text-left text-sm font-normal text-grey-800 outline-hidden transition-colors select-none hover:bg-grey-50',
          selected && 'bg-grey-50',
        )}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <ScmProviderIcon size={12} />
          <span className="truncate">{repo.fullName}</span>
        </span>
        {selected && <Check className="size-4 shrink-0 text-grey-800" />}
      </button>
    );
  };

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setSearch('');
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          aria-describedby={ariaDescribedBy}
          disabled={disabled}
          className="w-full justify-between font-normal"
          suffixIcon={<ChevronsUpDown className="ml-2 shrink-0 opacity-50" />}
        >
          <span className="truncate">{selectedRepo ? selectedRepo.fullName : placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <div className="flex items-center border-b px-3 py-2">
          <Search className="mr-2 size-3.5 shrink-0 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search repositories..."
            aria-label="Search repositories"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1" role="listbox">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground" role="status">
              No repositories found.
            </p>
          ) : suggested.length > 0 ? (
            <>
              <div role="group" aria-labelledby={suggestedId}>
                <p id={suggestedId} className="px-2 pt-1 pb-1 text-xs font-medium text-muted-foreground">
                  Suggested
                </p>
                {suggested.map(renderOption)}
              </div>
              {shown.length > 0 && (
                <div role="group" aria-labelledby={allId} className="mt-1 border-t border-border pt-1">
                  <p id={allId} className="px-2 pt-1 pb-1 text-xs font-medium text-muted-foreground">
                    All repositories
                  </p>
                  {shown.map(renderOption)}
                </div>
              )}
            </>
          ) : (
            shown.map(renderOption)
          )}
          {rest.length > shown.length && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              Showing {shown.length} of {rest.length.toLocaleString()}. Type to narrow the list.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
